<template>
    <a-modal title="" :width="'70%'" :open="$store.state.indexPop.visible" @cancel="cancel" :maskClosable="false"
        :footer="null" :bodyStyle="{ padding: '0', maxHeight: '75vh', overflowY: 'auto' }"
        class="performance-todo-modal" centered>
        <!-- ============================================================ -->
        <!-- 1. 顶部 -->
        <!-- ============================================================ -->
        <div class="modal-header">
            <div class="header-left">
                <div class="avatar-wrapper">
                    <a-avatar :size="48" style="background: #667eea; vertical-align: middle">
                        {{ userInitial }}
                    </a-avatar>
                    <span class="online-dot"></span>
                </div>
                <div class="greeting">
                    <div class="greeting-text">
                        欢迎回来，{{ userName }} 👋
                        <span class="sub-greeting">工单待办提醒</span>
                    </div>
                    <div class="header-meta">
                        <span class="dept-tag">🏢 核心代理商</span>
                        <span class="divider">|</span>
                        <span class="date-time">{{ currentDateTime }}</span>
                    </div>
                </div>
            </div>
            <div class="header-right">
                <div class="health-score">
                    <span class="score-label">代理合规指数</span>
                    <span class="score-value" :class="scoreStatus">{{ complianceScore }}</span>
                    <a-tooltip title="基于工单超时率、资料完整度综合评估">
                        <QuestionCircleOutlined style="color: #bfbfbf; margin-left: 4px" />
                    </a-tooltip>
                </div>
            </div>
        </div>

        <!-- ============================================================ -->
        <!-- 2. 统计看板 -->
        <!-- ============================================================ -->
        <div class="stats-row">
            <div class="stat-card" :class="{ active: filterType === 'all' }" @click="filterType = 'all'">
                <div class="stat-num">{{ totalCount }}</div>
                <div class="stat-label">📋 全部待办</div>
            </div>
            <div class="stat-card" :class="{ active: filterType === 'urgent' }" @click="filterType = 'urgent'">
                <div class="stat-num urgent">{{ urgentCount }}</div>
                <div class="stat-label">⏳ 即将到期</div>
                <div class="stat-trend">近3天需处理</div>
            </div>
            <div class="stat-card" :class="{ active: filterType === 'files' }" @click="filterType = 'files'">
                <div class="stat-num files">{{ missingFilesList.length }}</div>
                <div class="stat-label">📎 待补充资料</div>
                <div class="stat-trend danger">逾期扣款中</div>
            </div>
            <div class="stat-card" :class="{ active: filterType === 'payment' }" @click="filterType = 'payment'">
                <div class="stat-num payment">{{ periodList.length }}</div>
                <div class="stat-label">💳 超期待付款</div>
                <div class="stat-trend">需尽快支付</div>
            </div>
            <div class="stat-card" :class="{ active: filterType === 'setup' }" @click="filterType = 'setup'">
                <div class="stat-num setup">{{ unfinishList.length }}</div>
                <div class="stat-label">📌 未办结设立</div>
                <div class="stat-trend">待办理事项</div>
            </div>
        </div>

        <!-- ============================================================ -->
        <!-- 3. 筛选工具栏 -->
        <!-- ============================================================ -->
        <div class="toolbar">
            <div class="filter-tabs">
                <a-radio-group v-model:value="filterType" button-style="solid" size="small">
                    <a-radio-button value="all">全部</a-radio-button>
                    <a-radio-button value="work">工单</a-radio-button>
                    <a-radio-button value="files">资料</a-radio-button>
                    <a-radio-button value="payment">付款</a-radio-button>
                    <a-radio-button value="setup">设立</a-radio-button>
                </a-radio-group>
            </div>
            <div class="toolbar-right">
                <a-input-search placeholder="搜索待办标题" size="small" style="width: 200px" v-model:value="searchKeyword" />
                <a-button type="link" size="small" @click="handleExport">📤 导出待办</a-button>
            </div>
        </div>

        <!-- ============================================================ -->
        <!-- 4. 待办列表 -->
        <!-- ============================================================ -->
        <div class="todo-list-wrapper">

            <!-- ========================================================== -->
            <!-- 板块1：未办结工单（压缩为2行） -->
            <!-- ========================================================== -->
            <div class="todo-section">
                <div class="section-header" @click="toggleCollapse('work')">
                    <div class="section-title">
                        <span class="title-icon">📋</span> 未办结工单
                        <a-tag color="blue" class="count-tag">{{ filteredWorkList.length }}</a-tag>
                    </div>
                    <span class="collapse-icon">{{ collapseState.work ? '▾' : '▸' }}</span>
                </div>
                <div class="todo-items" v-if="collapseState.work">
                    <div v-for="(item, index) in processedWorkList" :key="'work-' + item.id" class="todo-item"
                        :class="item.priorityClass">
                        <div class="item-left">
                            <div class="item-index">{{ index + 1 }}</div>
                            <div class="item-content">
                                <!-- 第1行：标题 + 状态 + 代理商 + 截止日期 + 开始日期 + 录入日期 + 分类 + 扣款 -->
                                <div class="item-title">
                                    <span class="title-text">{{ item.title }}</span>
                                    <span v-if="item.daysDiff <= 3 && item.daysDiff >= 0" class="urgent-badge">紧急</span>
                                    <span class="meta-status" :class="item.statusClass">{{ item.statusText }}</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-agent">👤 {{ item.agent }}</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-entry">📥 录入：{{ formatDate(item.entry_date) }}</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-deadline">⏰ 截止：{{ formatDate(item.deadline) }}</span>
                                    <span class="countdown" :class="item.countdownClass">({{ item.countdownText
                                    }})</span>
                                    <span class="meta-divider">|</span>
                                    <a-tag :color="getCategoryColor(item.category)" class="category-tag">{{
                                        item.category }}</a-tag>
                                    <!-- 扣款放在行尾 -->
                                    <span class="penalty-inline" v-if="item.overdueDays > 0">
                                        🚨 已超期 <strong>{{ item.overdueDays }}</strong> 天，扣款 <strong
                                            class="penalty-amount">¥{{ item.penalty }}</strong>
                                    </span>
                                </div>
                                <!-- 第2行：关联主体 | 地址 | 标签 -->
                                <div class="item-meta">
                                    <span class="meta-entity">🏛️ 关联主体：{{ item.entity_name }}</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-address">📍 入驻地址：{{ item.address_short || '岳麓区梅溪湖街道看云路465号'
                                    }}</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-tags-label">🏷️ 标签：</span>
                                    <a-tag color="orange" v-for="tag in item.tags" :key="tag" class="tag-item">{{ tag
                                    }}</a-tag>
                                </div>
                            </div>
                        </div>
                        <div class="item-right">
                            <a-button type="primary" size="small" @click.stop="handleGo(item)">去处理</a-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ========================================================== -->
            <!-- 板块2：待补充资料（1行，扣款颜色与工单保持一致-红色） -->
            <!-- ========================================================== -->
            <div class="todo-section files-section">
                <div class="section-header" @click="toggleCollapse('files')">
                    <div class="section-title">
                        <span class="title-icon">📎</span> 待补充资料（合同 / 营业执照）
                        <a-tag color="purple" class="count-tag">{{ filteredMissingFilesList.length }}</a-tag>
                    </div>
                    <span class="collapse-icon">{{ collapseState.files ? '▾' : '▸' }}</span>
                </div>
                <div class="todo-items" v-if="collapseState.files">
                    <div v-for="(item, index) in processedMissingFilesList" :key="'file-' + item.id"
                        class="todo-item file-item">
                        <div class="item-left">
                            <div class="item-index">{{ index + 1 }}</div>
                            <div class="item-content">
                                <div class="item-meta one-line">
                                    <span class="meta-title">{{ item.name }}</span>
                                    <span class="penalty-inline" v-if="item.overdueDays > 0">
                                        🚨 已超期 <strong>{{ item.overdueDays }}</strong> 天，扣款 <strong
                                            class="penalty-amount">¥{{ item.penalty }}</strong>
                                    </span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-time">🕐 录入：{{ formatDate(item.entry_date) }}</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-cert">📄 成立：{{ formatDate(item.cert_date) }}</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-expiry">⏳ 到期未上传扣款截止日期：{{ formatDate(item.expiry_date) }}</span>
                                    <span class="meta-divider">|</span>
                                    <span class="upload-inline">
                                        <template v-for="fileType in item.missing_files" :key="fileType">
                                            <span class="file-icon" v-if="fileType === '营业执照'">📄</span>
                                            <span class="file-icon" v-else-if="fileType === '租赁合同'">📋</span>
                                            <span class="file-icon" v-else>📎</span>
                                            <span class="file-label">{{ fileType }}</span>
                                            <a-button size="small" type="primary" ghost class="upload-btn"
                                                @click.stop="triggerUpload($event, item, fileType)">上传</a-button>
                                            <input type="file" ref="fileInputs" style="display:none"
                                                accept=".pdf,.doc,.docx,.jpg,.png"
                                                @change="handleFileChange($event, item, fileType)" />
                                            <a-tag v-if="item.uploadedFiles && item.uploadedFiles.includes(fileType)"
                                                color="green" size="small">已传</a-tag>
                                            <span class="upload-divider"
                                                v-if="!isLastFile(item.missing_files, fileType)">|</span>
                                        </template>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ========================================================== -->
            <!-- 板块3：超期待付款单（1行） -->
            <!-- ========================================================== -->
            <div class="todo-section">
                <div class="section-header" @click="toggleCollapse('payment')">
                    <div class="section-title">
                        <span class="title-icon">💰</span> 超期待付款单
                        <a-tag color="orange" class="count-tag">{{ filteredPeriodList.length }}</a-tag>
                    </div>
                    <span class="collapse-icon">{{ collapseState.payment ? '▾' : '▸' }}</span>
                </div>
                <div class="todo-items" v-if="collapseState.payment">
                    <div v-for="(item, index) in filteredPeriodList" :key="'period-' + index" class="todo-item">
                        <div class="item-left">
                            <div class="item-index">{{ index + 1 }}</div>
                            <div class="item-content">
                                <div class="item-meta one-line">
                                    <span class="meta-title">{{ item.full_name }}</span>
                                    <!-- 未付款标签 -->
                                    <span class="unpaid-badge">未付款</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-phone">
                                        <CopyOutlined class="copy-icon" />
                                        {{ item.phone }}
                                    </span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-time">🕐 录入：{{ formatDate(item.created_at) }}</span>
                                    <span class="meta-divider">|</span>
                                    <a-tag :color="item.tax_type === '一般纳税人' ? 'blue' : 'green'" class="category-tag">{{
                                        item.tax_type }}</a-tag>
                                    <span class="meta-divider">|</span>
                                    <a-tag :color="item.reg_type === '包注册' ? 'purple' : 'cyan'" class="category-tag">{{
                                        item.reg_type }}</a-tag>
                                    <span class="meta-divider">|</span>
                                    <a-tag :color="item.biz_type === '设立' ? 'green' : 'orange'" class="category-tag">{{
                                        item.biz_type }}</a-tag>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-address">📍 {{ item.address_short || '岳麓区梅溪湖街道看云路465号' }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="item-right">
                            <a-button type="primary" danger size="small" class="pay-btn"
                                @click.stop="handlePay(item)">立即付款</a-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ========================================================== -->
            <!-- 板块4：未办结设立或迁入（1行） -->
            <!-- ========================================================== -->
            <div class="todo-section setup-section">
                <div class="section-header" @click="toggleCollapse('setup')">
                    <div class="section-title">
                        <span class="title-icon">📌</span> 未办结设立或迁入
                        <a-tag color="cyan" class="count-tag">{{ filteredSetupList.length }}</a-tag>
                    </div>
                    <span class="collapse-icon">{{ collapseState.setup ? '▾' : '▸' }}</span>
                </div>
                <div class="todo-items" v-if="collapseState.setup">
                    <div v-for="(item, index) in filteredSetupList" :key="'setup-' + index"
                        class="todo-item setup-item">
                        <div class="item-left">
                            <div class="item-index">{{ index + 1 }}</div>
                            <div class="item-content">
                                <div class="item-meta one-line">
                                    <span class="meta-title">{{ item.full_name }}</span>
                                    <span class="setup-badge">待办理</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-time">📅 创建：{{ formatDate(item.created_at) }}</span>
                                    <span class="meta-divider">|</span>
                                    <span class="meta-remind">💬 {{ item.remind || '请及时办理设立或迁入手续或办结本次申请' }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="item-right">
                            <a-button type="primary" size="small" @click.stop="handleGoNewTab(item)">去处理</a-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 空状态 -->
            <div v-if="isEmpty" class="empty-state">
                <a-empty description="🎉 太棒了！当前没有匹配的待办事项" />
            </div>
        </div>

        <!-- ============================================================ -->
        <!-- 5. 底部操作栏 -->
        <!-- ============================================================ -->
        <div class="modal-footer">
            <div class="footer-left">
                <span class="footer-tip">💡 超期工单及资料将产生违约金，请尽快处理</span>
            </div>
            <div class="footer-right">
                <a-button @click="cancel">稍后处理</a-button>
                <a-button type="primary" @click="handleModalOk">我知道了</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script lang="ts" setup>
import { message } from 'ant-design-vue'
import { reactive, onMounted, ref, computed } from 'vue'
import { useStore } from 'vuex'
import { QuestionCircleOutlined, CopyOutlined } from '@ant-design/icons-vue'

const $store = useStore()
const emit = defineEmits(['cancel', 'ok'])

// ================================================================
// 折叠状态
// ================================================================
const collapseState = reactive({
    work: true,
    files: false,
    payment: false,
    setup: false
})

const toggleCollapse = (key: string) => {
    collapseState[key] = !collapseState[key]
}

// ================================================================
// 状态
// ================================================================
const filterType = ref<'all' | 'work' | 'files' | 'payment' | 'setup' | 'urgent'>('all')
const searchKeyword = ref('')
const fileInputs = ref<HTMLInputElement[]>([])

// ================================================================
// 用户信息
// ================================================================
const userName = ref('王代理')
const userInitial = computed(() => userName.value.charAt(0))

// ================================================================
// 工具函数
// ================================================================
const getDaysDiff = (dateStr: string) => {
    if (!dateStr) return 999
    const target = new Date(dateStr)
    const now = new Date()
    target.setHours(0, 0, 0, 0)
    now.setHours(0, 0, 0, 0)
    return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const getOverdueDays = (dateStr: string) => {
    if (!dateStr) return 0
    const target = new Date(dateStr)
    const now = new Date()
    target.setHours(0, 0, 0, 0)
    now.setHours(0, 0, 0, 0)
    return Math.max(0, Math.ceil((now.getTime() - target.getTime()) / (1000 * 60 * 60 * 24)))
}

const getPenalty = (dateStr: string) => {
    const days = getOverdueDays(dateStr)
    if (days <= 0) return 0
    return 20 + (days - 1) * 5
}

const getStatusClass = (daysDiff: number) => {
    if (daysDiff < 0) return 'error'
    if (daysDiff <= 2) return 'warning'
    return 'processing'
}
const getStatusText = (daysDiff: number) => {
    if (daysDiff < 0) return '已超期'
    if (daysDiff <= 2) return '即将到期'
    return '处理中'
}
const getPriorityClass = (daysDiff: number) => {
    if (daysDiff < 0) return 'priority-overdue'
    if (daysDiff <= 2) return 'priority-urgent'
    return ''
}
const getCountdownText = (daysDiff: number) => {
    if (daysDiff < 0) return `已超期 ${Math.abs(daysDiff)} 天`
    if (daysDiff === 0) return '今天到期'
    return `${daysDiff} 天后到期`
}
const getCountdownClass = (daysDiff: number) => {
    if (daysDiff < 0) return 'overdue'
    if (daysDiff <= 2) return 'urgent'
    return ''
}

const getCategoryColor = (category: string) => {
    const map: Record<string, string> = { '投诉': 'red', '举报': 'orange', '代办事项': 'blue' }
    return map[category] || 'default'
}

const isLastFile = (files: string[], fileType: string) => {
    return files.indexOf(fileType) === files.length - 1
}

// ================================================================
// 数据获取
// ================================================================
const popupData = computed(() => {
    const base = $store.state.indexPop.popupData || {
        workOrder_list: [],
        period_list: [],
        unfinish_list: [],
        missing_files_list: []
    }

    // 工单数据
    if (base.workOrder_list && base.workOrder_list.length > 0) {
        base.workOrder_list = base.workOrder_list.map((item: any) => ({
            ...item,
            agent: item.agent || '李华',
            entry_date: item.entry_date || '2026-08-01',
            category: item.category || '代办事项',
            entity_name: item.entity_name || '长沙信玖信息技术有限公司',
            address_short: item.address_short || '岳麓区梅溪湖街道看云路465号',
            tags: item.tags || ['普通经营纠纷'],
            deadline: item.deadline || '2026-07-20'
        }))
    } else {
        base.workOrder_list = [
            { id: 1, title: '客户投诉处理工单 - XX科技', agent: '李华', deadline: '2026-07-20', entry_date: '2026-07-01', category: '投诉', entity_name: '长沙信玖信息技术有限公司', address_short: '岳麓区梅溪湖街道看云路465号', tags: ['普通经营纠纷'] },
            { id: 2, title: '违规举报处理 - YY商贸', agent: '张明', deadline: '2026-08-18', entry_date: '2026-08-10', category: '举报', entity_name: '长沙信玖信息技术有限公司', address_short: '岳麓区梅溪湖街道看云路465号', tags: ['普通经营纠纷', '加急'] },
            { id: 3, title: '代办事项 - ZZ供应链', agent: '陈芳', deadline: '2026-08-10', entry_date: '2026-08-01', category: '代办事项', entity_name: '长沙信玖信息技术有限公司', address_short: '岳麓区梅溪湖街道看云路465号', tags: ['普通经营纠纷'] }
        ]
    }

    // 待付款单（删除 remark 字段，改用 unpaid 标记）
    if (!base.period_list || base.period_list.length === 0) {
        base.period_list = [
            { full_name: 'A级供应商货款（XX科技）', phone: '13912345678', created_at: '2026-08-15', address_short: '岳麓区梅溪湖街道看云路465号', tax_type: '一般纳税人', reg_type: '包注册', biz_type: '设立', unpaid: true },
            { full_name: 'B级服务商尾款（YY商贸）', phone: '13798765432', created_at: '2026-08-18', address_short: '岳麓区梅溪湖街道看云路465号', tax_type: '小规模纳税人', reg_type: '裸地址', biz_type: '迁入', unpaid: true },
            { full_name: 'C级代理保证金（ZZ供应链）', phone: '13655556666', created_at: '2026-08-20', address_short: '岳麓区梅溪湖街道看云路465号', tax_type: '一般纳税人', reg_type: '包注册', biz_type: '设立', unpaid: true }
        ]
    }

    // 待补充资料
    if (!base.missing_files_list || base.missing_files_list.length === 0) {
        base.missing_files_list = [
            { id: 1, name: 'XX科技项目代理合同', missing_files: ['营业执照', '租赁合同'], entry_date: '2026-07-01', cert_date: '2026-07-05', expiry_date: '2026-07-20', uploadedFiles: [] },
            { id: 2, name: 'YY商贸代理协议', missing_files: ['营业执照'], entry_date: '2026-07-15', cert_date: '2026-07-20', expiry_date: '2026-08-01', uploadedFiles: [] },
            { id: 3, name: 'ZZ供应链服务合同', missing_files: ['租赁合同'], entry_date: '2026-08-01', cert_date: '2026-08-05', expiry_date: '2026-08-10', uploadedFiles: [] }
        ]
    }

    // 未办结设立
    if (!base.unfinish_list || base.unfinish_list.length === 0) {
        base.unfinish_list = [
            { full_name: 'XX科技设立登记', created_at: '2026-08-01', remind: '请及时办理设立或迁入手续或办结本次申请' },
            { full_name: 'YY商贸迁入手续', created_at: '2026-08-10', remind: '请及时办理设立或迁入手续或办结本次申请' }
        ]
    }

    return base
})

// ================================================================
// 列表数据
// ================================================================
const workList = computed(() => popupData.value.workOrder_list || [])
const periodList = computed(() => popupData.value.period_list || [])
const unfinishList = computed(() => popupData.value.unfinish_list || [])
const missingFilesList = computed(() => popupData.value.missing_files_list || [])

// ================================================================
// 预处理
// ================================================================
const processedWorkList = computed(() => {
    return filteredWorkList.value.map(item => {
        const daysDiff = getDaysDiff(item.deadline)
        const overdueDays = getOverdueDays(item.deadline)
        const penalty = getPenalty(item.deadline)
        return {
            ...item,
            daysDiff,
            overdueDays,
            penalty,
            statusClass: getStatusClass(daysDiff),
            statusText: getStatusText(daysDiff),
            priorityClass: getPriorityClass(daysDiff),
            countdownText: getCountdownText(daysDiff),
            countdownClass: getCountdownClass(daysDiff)
        }
    })
})

const processedMissingFilesList = computed(() => {
    return filteredMissingFilesList.value.map(item => {
        const overdueDays = getOverdueDays(item.expiry_date)
        const penalty = getPenalty(item.expiry_date)
        return {
            ...item,
            overdueDays,
            penalty
        }
    })
})

// ================================================================
// 计算属性
// ================================================================
const totalCount = computed(() => workList.value.length + periodList.value.length + unfinishList.value.length + missingFilesList.value.length)

const urgentCount = computed(() => {
    return workList.value.filter(item => {
        if (!item.deadline) return false
        const diff = getDaysDiff(item.deadline)
        return diff >= 0 && diff <= 3
    }).length
})

const complianceScore = computed(() => {
    const base = 100
    const urgentPenalty = urgentCount.value * 1
    const overdueWorkCount = workList.value.filter(item => getOverdueDays(item.deadline) > 0).length
    const overdueFileCount = missingFilesList.value.filter(item => getOverdueDays(item.expiry_date) > 0).length
    const filePenalty = (overdueWorkCount + overdueFileCount) * 3
    return Math.max(40, base - urgentPenalty - filePenalty)
})

const scoreStatus = computed(() => {
    const s = complianceScore.value
    if (s >= 85) return 'high'
    if (s >= 65) return 'mid'
    return 'low'
})

const currentDateTime = computed(() => {
    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`
})

// ================================================================
// 过滤逻辑
// ================================================================
const filteredWorkList = computed(() => {
    if (filterType.value === 'files' || filterType.value === 'payment' || filterType.value === 'setup') return []
    let list = workList.value
    if (filterType.value === 'urgent') {
        list = list.filter(item => {
            if (!item.deadline) return false
            const diff = getDaysDiff(item.deadline)
            return diff >= 0 && diff <= 3
        })
    }
    if (searchKeyword.value) {
        list = list.filter(item => item.title?.includes(searchKeyword.value))
    }
    return list
})

const filteredMissingFilesList = computed(() => {
    if (filterType.value === 'work' || filterType.value === 'payment' || filterType.value === 'setup' || filterType.value === 'urgent') return []
    if (filterType.value === 'all' || filterType.value === 'files') {
        let list = missingFilesList.value
        if (searchKeyword.value) {
            list = list.filter(item => item.name?.includes(searchKeyword.value))
        }
        return list
    }
    return []
})

const filteredPeriodList = computed(() => {
    if (filterType.value === 'files' || filterType.value === 'work' || filterType.value === 'setup' || filterType.value === 'urgent') return []
    if (filterType.value === 'all' || filterType.value === 'payment') {
        let list = periodList.value
        if (searchKeyword.value) {
            list = list.filter(item => item.full_name?.includes(searchKeyword.value))
        }
        return list
    }
    return []
})

const filteredSetupList = computed(() => {
    if (filterType.value === 'work' || filterType.value === 'payment' || filterType.value === 'files' || filterType.value === 'urgent') return []
    if (filterType.value === 'all' || filterType.value === 'setup') {
        let list = unfinishList.value
        if (searchKeyword.value) {
            list = list.filter(item => item.full_name?.includes(searchKeyword.value))
        }
        return list
    }
    return []
})

const isEmpty = computed(() => {
    return filteredWorkList.value.length === 0 &&
        filteredMissingFilesList.value.length === 0 &&
        filteredPeriodList.value.length === 0 &&
        filteredSetupList.value.length === 0
})

// ================================================================
// 文件上传
// ================================================================
const triggerUpload = (event: Event, item: any, fileType: string) => {
    const target = event.currentTarget as HTMLElement
    const uploadItem = target.closest('.upload-inline') || target.closest('.upload-item')
    if (!uploadItem) return
    const input = uploadItem.querySelector('input[type="file"]') as HTMLInputElement
    if (input) {
        input.click()
    }
}

const handleFileChange = (event: Event, item: any, fileType: string) => {
    const input = event.target as HTMLInputElement
    if (!input.files || input.files.length === 0) return
    const file = input.files[0]
    message.loading({ content: `正在上传 ${fileType}...`, key: 'upload' })
    setTimeout(() => {
        if (!item.uploadedFiles) item.uploadedFiles = []
        if (!item.uploadedFiles.includes(fileType)) {
            item.uploadedFiles.push(fileType)
        }
        message.success({ content: `${fileType} 上传成功！`, key: 'upload' })
        input.value = ''
    }, 1000)
}

// ================================================================
// 事件处理
// ================================================================
const cancel = () => $store.dispatch('indexPop/closePopup')
const handleModalOk = () => $store.dispatch('indexPop/closePopup')
const handleGo = (item: any) => message.success(`正在跳转: ${item.title || item.full_name}`)
const handleGoNewTab = (item: any) => {
    window.open(`https://example.com/todo/${encodeURIComponent(item.full_name)}`, '_blank')
    message.info(`已在新标签页打开: ${item.full_name}`)
}
const handlePay = (item: any) => message.success(`正在跳转付款: ${item.full_name}`)
const handleExport = () => message.success('正在导出待办清单...')

onMounted(() => { })
</script>

<style lang="less" scoped>
// ================================================================
// 全局样式
// ================================================================
.performance-todo-modal {
    :deep(.ant-modal-content) {
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 24px 80px rgba(0, 0, 0, 0.18);
    }

    :deep(.ant-modal-body) {
        padding: 0;
    }

    :deep(.ant-modal-body::-webkit-scrollbar) {
        width: 4px;
    }

    :deep(.ant-modal-body::-webkit-scrollbar-thumb) {
        background: #d9d9d9;
        border-radius: 4px;
    }
}

// ================================================================
// 头部
// ================================================================
.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 32px 20px;
    background: linear-gradient(135deg, #f5f7ff 0%, #ffffff 100%);
    border-bottom: 1px solid #f0f0f0;

    .header-left {
        display: flex;
        align-items: center;
        gap: 16px;

        .avatar-wrapper {
            position: relative;

            .online-dot {
                position: absolute;
                bottom: 2px;
                right: 2px;
                width: 12px;
                height: 12px;
                background: #52c41a;
                border-radius: 50%;
                border: 2px solid #fff;
            }
        }

        .greeting {
            .greeting-text {
                font-size: 18px;
                font-weight: 600;
                color: #1a1a2e;

                .sub-greeting {
                    font-weight: 400;
                    font-size: 14px;
                    color: #8c8c8c;
                    margin-left: 8px;
                }
            }

            .header-meta {
                margin-top: 2px;
                font-size: 13px;
                color: #8c8c8c;

                .dept-tag {
                    background: #e6f7ff;
                    color: #1890ff;
                    padding: 0 8px;
                    border-radius: 4px;
                }

                .divider {
                    margin: 0 8px;
                }
            }
        }
    }

    .header-right {
        .health-score {
            background: #fff;
            padding: 6px 18px 6px 16px;
            border-radius: 30px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            border: 1px solid #f0f0f0;

            .score-label {
                font-size: 13px;
                color: #8c8c8c;
                margin-right: 8px;
            }

            .score-value {
                font-size: 20px;
                font-weight: 700;

                &.high {
                    color: #52c41a;
                }

                &.mid {
                    color: #faad14;
                }

                &.low {
                    color: #ff4d4f;
                }
            }
        }
    }
}

// ================================================================
// 统计看板
// ================================================================
.stats-row {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
    padding: 20px 32px;
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;

    .stat-card {
        background: #fff;
        border-radius: 14px;
        padding: 16px 20px;
        cursor: pointer;
        transition: all 0.25s ease;
        border: 2px solid transparent;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.02);

        &:hover {
            transform: translateY(-3px);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
        }

        &.active {
            border-color: #667eea;
            background: #f8f9ff;
        }

        .stat-num {
            font-size: 28px;
            font-weight: 700;
            color: #1a1a2e;
            line-height: 1.2;

            &.urgent {
                color: #fa8c16;
            }

            &.files {
                color: #722ed1;
            }

            &.payment {
                color: #1890ff;
            }

            &.setup {
                color: #13c2c2;
            }
        }

        .stat-label {
            font-size: 14px;
            color: #4a4a5a;
            margin-top: 2px;
        }

        .stat-trend {
            font-size: 12px;
            color: #8c8c8c;
            margin-top: 4px;

            &.danger {
                color: #ff4d4f;
                font-weight: 500;
            }
        }
    }
}

// ================================================================
// 工具栏
// ================================================================
.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 32px;
    background: #ffffff;
    border-bottom: 1px solid #f0f0f0;

    .filter-tabs {
        :deep(.ant-radio-group) {
            .ant-radio-button-wrapper {
                height: 28px;
                line-height: 26px;
                padding: 0 14px;
            }
        }
    }

    .toolbar-right {
        display: flex;
        align-items: center;
        gap: 12px;
    }
}

// ================================================================
// 待办列表
// ================================================================
.todo-list-wrapper {
    padding: 16px 32px 8px;
}

.todo-section {
    margin-bottom: 12px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

    .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 16px;
        cursor: pointer;
        user-select: none;
        border-radius: 12px;

        .section-title {
            font-size: 15px;
            font-weight: 600;
            color: #1a1a2e;
            display: flex;
            align-items: center;
            gap: 8px;

            .title-icon {
                font-size: 18px;
            }

            .count-tag {
                font-weight: 500;
            }
        }

        .collapse-icon {
            font-size: 16px;
            color: #8c8c8c;
            margin-left: 12px;
        }
    }

    .todo-items {
        padding: 0 16px 12px 16px;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    &.files-section {
        .section-header .section-title {
            color: #722ed1;
        }

        .todo-item.file-item {
            border-left-color: #722ed1;
            background: #f9f0ff;
        }
    }

    &.setup-section {
        .section-header .section-title {
            color: #13c2c2;
        }

        .todo-item.setup-item {
            border-left-color: #13c2c2;
            background: #f0faff;
        }
    }
}

// ================================================================
// 待办项
// ================================================================
.todo-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 14px;
    background: #fafbfc;
    border-radius: 10px;
    border-left: 4px solid transparent;

    &.priority-urgent {
        border-left-color: #faad14;
        background: #fffbe6;
    }

    &.priority-overdue {
        border-left-color: #ff4d4f;
        background: #fff1f0;
    }

    &.file-item {
        border-left-color: #722ed1;
        background: #f9f0ff;
    }

    &.setup-item {
        border-left-color: #13c2c2;
        background: #f0faff;
    }

    .item-left {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        min-width: 0;

        .item-index {
            width: 20px;
            height: 20px;
            background: #e8e8e8;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            color: #8c8c8c;
            flex-shrink: 0;
        }

        .item-content {
            flex: 1;
            min-width: 0;

            .item-title {
                font-size: 13px;
                font-weight: 500;
                color: #1a1a2e;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 4px 6px;
                line-height: 1.6;

                .title-text {
                    font-weight: 600;
                }

                .urgent-badge {
                    background: #ff4d4f;
                    color: #fff;
                    font-size: 10px;
                    padding: 0 8px;
                    border-radius: 10px;
                    font-weight: 500;
                }

                .meta-status {
                    padding: 0 8px;
                    border-radius: 10px;
                    font-size: 10px;
                    font-weight: 500;

                    &.warning {
                        background: #fff7e6;
                        color: #fa8c16;
                    }

                    &.error {
                        background: #fff1f0;
                        color: #ff4d4f;
                    }

                    &.processing {
                        background: #e6f7ff;
                        color: #1890ff;
                    }
                }

                .meta-divider {
                    color: #d9d9d9;
                }

                .meta-agent,
                .meta-deadline,
                .meta-start,
                .meta-entry {
                    color: #4a4a5a;
                    font-size: 12px;
                    font-weight: 400;
                }

                .meta-deadline {
                    font-weight: 500;
                }

                .countdown {
                    font-weight: 500;
                    font-size: 10px;

                    &.urgent {
                        color: #fa8c16;
                    }

                    &.overdue {
                        color: #ff4d4f;
                    }
                }

                .category-tag {
                    font-size: 10px;
                    margin: 0;
                    padding: 0 6px;
                    line-height: 18px;
                }

                // ============================================================
                // 统一扣款文字颜色 - 红色 (#ff4d4f)
                // ============================================================
                .penalty-inline {
                    font-size: 11px;
                    color: #ff4d4f;
                    margin-left: 4px;

                    strong {
                        font-weight: 700;
                        color: #ff4d4f;
                    }

                    .penalty-amount {
                        font-size: 13px;
                        color: #ff4d4f;
                    }
                }
            }

            .item-meta {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 3px 8px;
                margin-top: 1px;
                font-size: 12px;
                color: #8c8c8c;

                .meta-divider {
                    color: #d9d9d9;
                }

                .meta-entity,
                .meta-address {
                    color: #4a4a5a;
                }

                .meta-tags-label {
                    color: #4a4a5a;
                }

                .tag-item {
                    font-size: 10px;
                    margin: 0 1px;
                    padding: 0 6px;
                    line-height: 16px;
                }

                .category-tag {
                    font-size: 10px;
                    margin: 0;
                    padding: 0 6px;
                    line-height: 18px;
                }

                // 一行展示
                &.one-line {
                    gap: 4px 8px;
                    flex-wrap: wrap;

                    .meta-title {
                        font-weight: 600;
                        color: #1a1a2e;
                    }

                    // ============================================================
                    // 待补充资料板块的扣款颜色继承 .penalty-inline 的红色
                    // ============================================================
                    .penalty-inline {
                        font-size: 11px;
                        color: #ff4d4f;
                        margin-left: 4px;

                        strong {
                            font-weight: 700;
                            color: #ff4d4f;
                        }

                        .penalty-amount {
                            font-size: 13px;
                            color: #ff4d4f;
                        }
                    }
                }

                // 上传行
                .upload-inline {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    flex-wrap: wrap;

                    .file-icon {
                        font-size: 14px;
                    }

                    .file-label {
                        font-size: 12px;
                        color: #4a4a5a;
                        font-weight: 500;
                    }

                    .upload-divider {
                        color: #d9d9d9;
                    }

                    :deep(.ant-btn-sm) {
                        height: 20px;
                        font-size: 10px;
                        padding: 0 8px;
                        line-height: 18px;
                    }

                    :deep(.ant-tag) {
                        font-size: 10px;
                        padding: 0 6px;
                        line-height: 16px;
                        margin: 0;
                    }
                }
            }

            .setup-badge {
                background: #13c2c2;
                color: #fff;
                font-size: 10px;
                padding: 0 8px;
                border-radius: 10px;
                font-weight: 500;
                display: inline-block;
                line-height: 20px;
            }

            .unpaid-badge {
                background: #ff4d4f;
                color: #fff;
                font-size: 10px;
                padding: 0 8px;
                border-radius: 10px;
                font-weight: 500;
                display: inline-block;
                line-height: 20px;
            }

            .copy-icon {
                font-size: 13px;
                color: #1890ff;
                cursor: pointer;
                margin-right: 2px;

                &:hover {
                    color: #40a9ff;
                }
            }
        }
    }

    .item-right {
        flex-shrink: 0;
        margin-left: 12px;

        :deep(.ant-btn-sm) {
            height: 26px;
            font-size: 12px;
            padding: 0 12px;
        }
    }
}

// ================================================================
// 空状态
// ================================================================
.empty-state {
    padding: 30px 0;
    text-align: center;
}

// ================================================================
// 底部
// ================================================================
.modal-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 32px 20px;
    border-top: 1px solid #f0f0f0;
    background: #fafbfc;

    .footer-left .footer-tip {
        color: #8c8c8c;
        font-size: 13px;
    }

    .footer-right {
        display: flex;
        gap: 12px;
    }
}

// ================================================================
// 按钮修复
// ================================================================
.pay-btn {
    &:hover {
        color: #fff !important;
        background: #ff4d4f !important;
        border-color: #ff4d4f !important;
    }
}

.upload-btn {
    &:hover {
        color: #1890ff !important;
        background: #fff !important;
        border-color: #1890ff !important;
    }
}
</style>
